/* src/components/Integrations/Content/index.js */
import { humanize } from '../../../utils/words';
import useAccessToken from '../../../utils/useAccessToken';
import useLoader from '../../../utils/useLoader';
import Api from '../../../api';
import { useEffect, useState } from 'react';
import SchedulerService from '../../../api/scheduler';
import Button from '../../common/Button';
import Modal from '../../common/Modal';

import toast from 'react-hot-toast';
import SchedulesTable from './SchedulesTable';
import CredentialForm from './CredentialForm';
import ScheduleForm from './ScheduleForm';

const modals = {
  ADD_CREDENTIAL: 'add-credential',
  AUTHORIZE: 'authorize',
  SCHEDULE: 'schedule',
  UPDATE_SCHEDULE: 'update-schedule',
}

// ---------------------------------------------------------------------
// Main wrapper – keeps the upload UI you already had
// ---------------------------------------------------------------------
function Content({ selectedCarrier, carriers }) {
  const { isLoading, setIsLoading } = useLoader();
  const { userData } = useAccessToken();
  const [credentials, setCredentials] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState("");
  const [selectedCredential, setSelectedCredential] = useState({});
  const [authParams, setAuthParams] = useState(carriers.find(carrier => carrier.id === selectedCarrier)?.auth_params || []);
  const [expandedRows, setExpandedRows] = useState([]);
  const [schedulesByCredential, setSchedulesByCredential] = useState({});
  const [selectedSchedule, setSelectedSchedule] = useState({});

  // Fetch all schedules once when credentials change
  useEffect(() => {
    fetchSchedules();
  }, [credentials]);

  const handleRowClick = (credentialId) => {
    setExpandedRows(prev =>
      prev.includes(credentialId)
        ? prev.filter(id => id !== credentialId)
        : [...prev, credentialId]
    );
  };

  const fetchSchedules = async () => {
    const response = await SchedulerService.WebScrapers.getAllWebScrapersSchedule();
    if (response.success) {
      // Group schedules by credential id (assuming credential_id field exists)
      const grouped = {};
      response.data.forEach(sch => {
        const credId = sch.body.credential_id;
        if (!grouped[credId]) grouped[credId] = [];
        grouped[credId].push(sch);
      });
      setSchedulesByCredential(grouped);
    } else {
      setSchedulesByCredential({});
    }
  };

  const handleCreateSchedule = async (jobData) => {
    try {
      setIsLoading(true);

      const data = {
        schedule: jobData.schedule,
        timezone: jobData.timezone,
        org_id: userData.orgID,
        site_id: selectedCarrier,
        service_id: jobData.docType,
        credential_id: selectedCredential.id,
      }

      const response = await SchedulerService.WebScrapers.createWebScraperSchedule(data);
      if (!response.success) {
        throw new Error('Failed to create schedule');
      }

      fetchSchedules();
      setIsModalOpen("");
      setIsLoading(false);
      toast.success('Schedule has been created!');
    } catch (error) {
      setIsLoading(false);
      toast.error('Something went wrong while creating the schedule. Please try again or contact support.');
    }
  };

  const handleUpdateSchedule = async (jobData) => {
    try {
      setIsLoading(true);

      const jobId = btoa(jobData.name)

      const data = {
        schedule: jobData.schedule,
        timezone: jobData.timezone,
        org_id: userData.orgID,
        site_id: selectedCarrier,
        service_id: jobData.docType,
        credential_id: selectedCredential.id,
        action: jobData.action
      }

      const response = await SchedulerService.WebScrapers.updateWebScraperSchedule(jobId, data);
      if (!response.success) {
        throw new Error('Failed to update schedule');
      }

      await fetchSchedules();
      setIsModalOpen("");
      setIsLoading(false);
      toast.success('Schedule has been updated!');
    } catch (error) {
      setIsLoading(false);
      toast.error('Something went wrong while updating the schedule. Please try again or contact support.');
    }
  };

  const getOrgCredentials = async () => {
    setIsLoading(true);

    const response = await Api.DataService.Orgs.getOrgSitesCredentials(userData.orgID, selectedCarrier)
    if (!response.success) {
      setCredentials([]);
      setIsLoading(false);
      return;
    }

    const credentials = response.data.filter(({ username }) => !username)
    setCredentials(credentials || []);
    setIsLoading(false);
  }

  const handleAddCredential = async (credentialData) => {
    try {
      setIsLoading(true);

      await Api.DataService.Orgs.addOrgSiteCredential(
        userData.orgID,
        selectedCarrier,
        credentialData
      );

      await getOrgCredentials();
      setIsModalOpen("");
      setIsLoading(false);
      toast.success('Credential has been added! You can now connect or validate it.');
    } catch (error) {
      setIsLoading(false);
      toast.error('Something went wrong while adding the credential. Please try again or contact support.');
    }
  }

  const handleDeleteCredential = async (credentialId) => {
    try {
      setIsLoading(true);

      await Api.DataService.Orgs.deleteOrgSiteCredential(
        userData.orgID,
        selectedCarrier,
        credentialId
      );

      await getOrgCredentials();
      setIsLoading(false);
      toast.success('Credential deleted! The list has been updated.');
    } catch (error) {
      setIsLoading(false);
      toast.error('Something went wrong while deleting the credential. Please try again or contact support.');
    }
  }

  const handleAuthorizeCredential = async (credentialData) => {
    try {
      setIsLoading(true);

      await Api.DataService.Orgs.updateOrgSiteCredential(
        userData.orgID,
        selectedCarrier,
        selectedCredential.id,
        credentialData
      );

      await Api.WebscrapersService.authorize(
        userData.orgID,
        selectedCarrier,
        selectedCredential.id
      )

      await getOrgCredentials();
      setIsModalOpen("");
      setIsLoading(false);
      toast.success('Credential authorized successfully!');
    } catch (error) {
      setIsLoading(false);
      toast.error('Something went wrong while authorizing the credential. Please try again or contact support.');
    }
  }

  const handleOpenModal = (modal) => {
    setIsModalOpen(modal);
  }

  const handleCloseModal = () => {
    setIsModalOpen("");
  }

  const handleValidateButtonClick = (credential) => {
    setSelectedCredential(credential);
    handleOpenModal(modals.AUTHORIZE);
  }

  const handleScheduleButtonClick = (credential) => {
    setSelectedCredential(credential);
    handleOpenModal(modals.SCHEDULE);
  }

  const handleUpdateScheduleButtonClick = (schedule) => {
    setSelectedSchedule(schedule);
    handleOpenModal(modals.UPDATE_SCHEDULE);
  }

  const handleDeleteSchedule = async (jobId) => {
    try {
      setIsLoading(true);

      const response = await SchedulerService.WebScrapers.deleteWebScraperSchedule(jobId);
      if (!response.success) {
        throw new Error('Failed to delete schedule');
      }

      fetchSchedules();
      setIsModalOpen("");
      setIsLoading(false);
      toast.success('Schedule has been deleted!');
    } catch (error) {
      setIsLoading(false);
      toast.error('Something went wrong while deleting the schedule. Please try again or contact support.');
    }
  }

  useEffect(() => {
    if (selectedCarrier !== 'Select a Carrier') {
      getOrgCredentials();
      setAuthParams(carriers.find(carrier => carrier.id === selectedCarrier)?.auth_params || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCarrier]);

  return (
    <>
      <h1>{humanize('Credentials')}</h1>

      { selectedCarrier !== 'Select a Carrier' && <Button 
        className="normal add-btn" 
        isLoading={isLoading} 
        label="Add" 
        onClick={() => handleOpenModal(modals.ADD_CREDENTIAL)}
      /> }

      <table>
        <thead>
          <th>ID</th>
          <th>Username</th>
          <th>Login?</th>
          <th>MFA?</th>
          <th>Last Updated At</th>
          <th>Last Working Date</th>
          <th>Action</th>
        </thead>
        <tbody>
          { credentials.map((credential, index) => (
            <>
              <tr
                key={credential.id}
                onClick={e => {
                  // Only expand/collapse if not clicking a button
                  if (e.target.tagName !== 'BUTTON' && e.target.tagName !== 'IMG') {
                    handleRowClick(credential.id);
                  }
                }}
              >
                  <td style={{ width: 32 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        style={{
                          transition: 'transform 0.2s',
                          transform: expandedRows.includes(credential.id) ? 'rotate(90deg)' : 'rotate(0deg)',
                          marginRight: 4,
                          verticalAlign: 'middle',
                          color: '#888',
                        }}
                      >
                        <path d="M5 3l6 5-6 5V3z" fill="currentColor" />
                      </svg>
                      {credential.id}
                    </span>
                  </td>
                <td>{credential.credential_data?.username}</td>
                <td>{credential.login_success ? 'Success' : 'Failed'}</td>
                <td>{credential.mfa_success ? 'Success' : 'Failed'}</td>
                <td>{credential.last_status_update ? credential.last_status_update : "N/A"}</td>
                <td>{credential.last_success_at ? credential.last_success_at : "N/A"}</td>
                <td>
                  <div>
                    { credential.last_success_at ? 
                      <Button className="normal" isLoading={isLoading} label="Validate" onClick={() => handleValidateButtonClick(credential)} /> : 
                      <Button className="normal" isLoading={isLoading} label="Connect" onClick={() => handleValidateButtonClick(credential)} />
                    }
                    <Button className="normal danger" isLoading={isLoading} label="Delete" onClick={() => handleDeleteCredential(credential.id)} />
                    { userData?.can_schedule && <Button className="normal" isLoading={isLoading} label="Schedule" onClick={() => handleScheduleButtonClick(credential)} /> }
                  </div>
                </td>
              </tr>
              {expandedRows.includes(credential.id) && (
                <SchedulesTable 
                  userData={userData} 
                  isLoading={isLoading} 
                  credential={credential} 
                  schedulesByCredential={schedulesByCredential} 
                  handleUpdateScheduleButtonClick={handleUpdateScheduleButtonClick}
                  handleDeleteSchedule={handleDeleteSchedule}
                  handleUpdateSchedule={handleUpdateSchedule}
                />
              )}
            </>
          )) }
          { credentials.length === 0 && (
              <tr>
                <td colSpan="7" className="no-data"><center>No Data Found</center></td>
              </tr> 
            )
          }
        </tbody>
      </table>

      <Modal
        isOpen={isModalOpen === modals.ADD_CREDENTIAL}
        onClose={handleCloseModal}
        title="Add New Credential"
      >
        <CredentialForm
          onSubmit={handleAddCredential}
          onCancel={handleCloseModal}
          isLoading={isLoading}
          data={{ mode: "Add" }}
          authParams={authParams}
        />
      </Modal>

      <Modal
        isOpen={isModalOpen === modals.AUTHORIZE}
        onClose={handleCloseModal}
        title="Validate Credential"
      >
        <CredentialForm
          onSubmit={handleAuthorizeCredential}
          onCancel={handleCloseModal}
          isLoading={isLoading}
          data={{ mode: "Validate", ...selectedCredential}}
          authParams={authParams}
        />
      </Modal>

      <Modal
        isOpen={isModalOpen === modals.SCHEDULE}
        onClose={handleCloseModal}
        title="Schedule Job"
      >
        <ScheduleForm
          userData={userData}
          onSubmit={handleCreateSchedule}
          onCancel={handleCloseModal}
          isLoading={isLoading}
        />
      </Modal>

      <Modal
        isOpen={isModalOpen === modals.UPDATE_SCHEDULE}
        onClose={handleCloseModal}
        title="Update Schedule Job"
      >
        <ScheduleForm
          userData={userData}
          onSubmit={handleUpdateSchedule}
          onCancel={handleCloseModal}
          isLoading={isLoading}
          data={{ mode: "Update", ...selectedSchedule }}
        />
      </Modal>
    </>
  );
}

export default Content;
